import { Alert, Box, Button, Typography } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDocument } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobilePurchaseDetail from '../../component/purchase/MobilePurchaseDetail';
import { useAppBarTitle } from '../../component/layout/AppBarActions';

function formatDateTime(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatCents(cents) {
    if (cents === null || cents === undefined) return '—';
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatIssueDate(value) {
    if (!value) return '';
    const [y, m, d] = String(value).split('-');
    return y && m && d ? `${d}/${m}/${y}` : '';
}

function InfoCard({ label, value, mono }) {
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '12px', px: 2, py: 1.25, minWidth: 140, bgcolor: 'background.paper' }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: mono ? '13px' : '16px', fontWeight: 700, wordBreak: 'break-all', fontFamily: mono ? 'monospace' : undefined }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}

export default function View() {
    const { reference } = useParams();
    const navigate = useNavigate();
    const isMobileShell = useIsMobileShell();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    useEffect(() => {
        setLoading(true);
        httpClient.get(`/purchases/${encodeURIComponent(reference)}`)
            .then((response) => setData(response.data))
            .catch((error) => {
                console.error('Error fetching purchase:', error);
                setData(null);
                setSnackbarMessage(error?.response?.status === 404 ? 'Compra não encontrada.' : 'Erro ao carregar compra.');
                setOpenSnackbar(true);
            })
            .finally(() => setLoading(false));
    }, [reference]);

    const hasValues = data?.total_cents !== null && data?.total_cents !== undefined;
    const isAccessKey = /^\d{44}$/.test(reference || '');

    useAppBarTitle(isMobileShell && data?.number ? `Compra NF ${data.number}` : '');

    const columns = [
        { path: 'code', name: 'Código', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{row.code}</Typography> },
        { path: 'name', name: 'Produto', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px' }}>{row.name}</Typography> },
        { path: 'quantity', name: 'Qtd. lançada', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px' }}>{row.quantity}</Typography> },
        { path: 'unit_cents', name: 'Valor unitário', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px' }}>{formatCents(row.unit_cents)}</Typography> },
        { path: 'total_cents', name: 'Valor total', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{formatCents(row.total_cents)}</Typography> },
    ];

    // Shell mobile: detalhe do protótipo `compras/detalhe.html`.
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[{ title: 'Compras', link: '/purchases' }]}
                titlePageTarget={data?.number ? `Compra NF ${data.number}` : 'Compra'}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity="error"
            >
                <MobilePurchaseDetail
                    loading={loading}
                    data={data}
                    hasValues={hasValues}
                    isAccessKey={isAccessKey}
                    supplierDocument={data?.supplier_document ? formatDocument(data.supplier_document) : ''}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[{ title: 'Compras', link: '/purchases' }]}
            titlePageTarget={data?.number ? `Compra NF ${data.number}` : 'Compra'}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity="error"
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Box>
                        <Typography component="h1" sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em' }}>
                            {data?.number ? `Compra NF ${data.number}${data.series ? ` / ${data.series}` : ''}` : 'Compra'}
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                            Itens com entrada lançada no estoque por esta nota.
                        </Typography>
                    </Box>
                    <Button variant="outlined" size="small" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('/purchases')}>
                        Voltar
                    </Button>
                </Box>

                {data && (
                    <>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                            <InfoCard label="Lançada em" value={formatDateTime(data.date)} />
                            <InfoCard label="Nota (nº / série)" value={data.number ? `${data.number}${data.series ? ` / ${data.series}` : ''}` : ''} />
                            <InfoCard label="Fornecedor" value={data.supplier} />
                            <InfoCard label="Produtos" value={data.items_count} />
                            <InfoCard label="Qtd. total lançada" value={data.total_quantity} />
                            {hasValues && (
                                <>
                                    <InfoCard label="Emissão" value={formatIssueDate(data.issue_date)} />
                                    <InfoCard label="CNPJ do fornecedor" value={data.supplier_document ? formatDocument(data.supplier_document) : ''} />
                                    <InfoCard label="Total da nota" value={formatCents(data.total_cents)} />
                                    <InfoCard label="Total dos itens lançados" value={formatCents(data.items_total_cents)} />
                                </>
                            )}
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <InfoCard label={isAccessKey ? 'Chave de acesso' : 'Referência (nota sem chave de acesso)'} value={data.reference} mono />
                        </Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {hasValues
                                ? 'Itens não lançados da nota não são exibidos. O valor unitário é o da nota (vUnCom) e o total do item é valor unitário × quantidade lançada.'
                                : 'Esta compra foi lançada antes do registro de valores: custos e valores da nota não foram guardados e itens não lançados não são exibidos.'}
                        </Alert>
                    </>
                )}

                <DefaultTable
                    colunms={columns}
                    data={data?.items || []}
                    iconsActions={[]}
                    loading={loading}
                    rowPerPage={15}
                    rowsPerPageOptions={[15, 30, 50]}
                    itemLabel="produtos"
                    sortableFields={['code', 'name', 'quantity', 'unit_cents', 'total_cents']}
                    defaultSort={{ field: 'name', direction: 'asc' }}
                />
            </Box>
        </PageScaffold>
    );
}
